<template>
  <div class="space-y-6">
    <!-- Header with status -->
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">
        {{ isNewContent ? 'Content Creation' : 'Content Optimization' }}
      </h3>
      <span v-if="optimization" class="px-3 py-1 rounded-full text-sm font-medium" :class="statusClass">
        {{ statusLabel }}
      </span>
    </div>

    <!-- Error -->
    <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
      <p class="text-sm text-red-700 dark:text-red-400">{{ error }}</p>
    </div>

    <!-- Progress bar during operations (not shown during pipeline — pipeline has its own progress) -->
    <div v-if="progressMessage && !isPipelineRunning" class="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-xl">
      <div class="flex items-center gap-3">
        <svg class="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span class="text-sm text-primary-700 dark:text-primary-300">{{ progressMessage }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <svg class="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>

    <!-- Phase 0: No optimization — choose path -->
    <div v-else-if="!optimization" class="text-center py-12">
      <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </div>
      <h4 class="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">Content Work</h4>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
        Choose whether to optimize existing page content or create new content from scratch.
      </p>
      <div class="flex items-center justify-center gap-4">
        <button
          @click="startOptimization"
          :disabled="starting"
          class="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ starting ? 'Starting...' : 'Optimize Existing Content' }}
        </button>
        <button
          @click="startContentCreation"
          :disabled="starting"
          class="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#12e40b] hover:bg-[#0fd609] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#12e40b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ starting ? 'Starting...' : 'Create New Content' }}
        </button>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- PATH A: Content Optimization (existing content) -->
    <!-- ============================================================ -->

    <!-- PIPELINE IN PROGRESS: Both paths show unified progress view -->
    <div v-else-if="isPipelineRunning" class="text-center py-12">
      <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
        <svg class="animate-spin w-8 h-8 text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
      <h4 class="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
        {{ isNewContent ? 'Creating Content...' : 'Optimizing Content...' }}
      </h4>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ progressMessage || 'AI is generating and assembling your content automatically.' }}
      </p>
      <p class="text-xs text-gray-400 mt-2">This may take a few minutes.</p>
    </div>

    <!-- ============================================================ -->
    <!-- SHARED: Completed state (matches all post-completed statuses) -->
    <!-- ============================================================ -->
    <div v-else-if="isContentCompleted" class="space-y-6">
      <div class="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl">
        <div class="flex items-center justify-between mb-4">
          <h4 class="text-base font-medium text-green-800 dark:text-green-300">
            {{ isNewContent ? 'Article Created' : 'Optimization Complete' }}
          </h4>
          <button
            @click="showArticle = !showArticle"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {{ showArticle ? 'Hide Article' : 'View Article' }}
          </button>
        </div>
        <div class="grid grid-cols-3 gap-4" :class="{ 'grid-cols-4 sm:grid-cols-4': optimization.llm_cost }">
          <div>
            <dt class="text-xs text-green-600 dark:text-green-400 uppercase tracking-wide">
              {{ isNewContent ? 'Sections Written' : 'Sections Optimized' }}
            </dt>
            <dd class="text-2xl font-semibold text-green-800 dark:text-green-200 mt-1">
              {{ optimization.summary?.sections_optimized || contentChanges.length || 0 }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-green-600 dark:text-green-400 uppercase tracking-wide">Total Words</dt>
            <dd class="text-2xl font-semibold text-green-800 dark:text-green-200 mt-1">
              {{ displayTotalWords }}
            </dd>
          </div>
          <div>
            <dt class="text-xs text-green-600 dark:text-green-400 uppercase tracking-wide">Keywords Met</dt>
            <dd class="text-2xl font-semibold text-green-800 dark:text-green-200 mt-1">
              {{ keywordsMetCount }}/{{ budgetItems.length }}
            </dd>
          </div>
          <div v-if="optimization.llm_cost">
            <dt class="text-xs text-green-600 dark:text-green-400 uppercase tracking-wide">AI Cost</dt>
            <dd class="text-2xl font-semibold text-green-800 dark:text-green-200 mt-1">
              ${{ formatCost(optimization.llm_cost.total_cost_cents) }}
            </dd>
          </div>
        </div>

        <!-- LLM Cost Breakdown -->
        <div v-if="optimization.llm_cost" class="mt-6 pt-4 border-t border-green-200 dark:border-green-800">
          <button @click="showCostBreakdown = !showCostBreakdown"
                  class="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 transition-colors">
            <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-90': showCostBreakdown }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
            AI Usage Details
            <span class="text-xs font-normal text-green-500 dark:text-green-400">
              ({{ optimization.llm_cost.total_tokens?.toLocaleString() }} tokens)
            </span>
          </button>
          <div v-if="showCostBreakdown" class="mt-3 space-y-1.5">
            <div v-for="item in optimization.llm_cost.breakdown" :key="item.step"
                 class="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-green-100/50 dark:bg-green-900/20">
              <div class="flex items-center gap-2">
                <span class="text-green-800 dark:text-green-200 font-medium">{{ formatStepName(item.step) }}</span>
                <span class="text-xs text-green-500 dark:text-green-400">&times;{{ item.calls }}</span>
              </div>
              <div class="flex items-center gap-4 text-xs text-green-600 dark:text-green-400">
                <span>{{ (item.input_tokens + item.output_tokens).toLocaleString() }} tok</span>
                <span class="font-medium text-green-800 dark:text-green-200">${{ formatCost(item.cost_cents) }}</span>
              </div>
            </div>
            <div class="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-green-200/60 dark:bg-green-800/40 border-t border-green-300 dark:border-green-700 mt-2">
              <div class="flex items-center gap-2">
                <span class="text-green-900 dark:text-green-100 font-semibold">Total</span>
                <span class="text-xs text-green-600 dark:text-green-400">&times;{{ optimization.llm_cost.breakdown.reduce((s, i) => s + i.calls, 0) }} calls</span>
              </div>
              <div class="flex items-center gap-4 text-xs">
                <span class="text-green-700 dark:text-green-300 font-medium">{{ optimization.llm_cost.total_tokens?.toLocaleString() }} tok</span>
                <span class="font-bold text-green-900 dark:text-green-100">${{ formatCost(optimization.llm_cost.total_cost_cents) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pre-audit results -->
        <div v-if="preAudit && preAudit.length" class="mt-6 pt-4 border-t border-green-200 dark:border-green-800">
          <h5 class="text-sm font-medium text-green-700 dark:text-green-300 mb-3">Pre-Audit Results</h5>
          <div class="space-y-2">
            <div v-for="check in preAudit" :key="check.check" class="flex items-center gap-2 text-sm">
              <span v-if="check.status === 'pass'" class="text-green-600">&#10003;</span>
              <span v-else-if="check.status === 'fail'" class="text-red-600">&#10007;</span>
              <span v-else class="text-amber-500">&#9888;</span>
              <span class="text-green-800 dark:text-green-200">{{ check.message }}</span>
            </div>
          </div>
        </div>

        <!-- Final budget -->
        <div v-if="budgetItems.length > 0" class="mt-6 pt-4 border-t border-green-200 dark:border-green-800">
          <h5 class="text-sm font-medium text-green-700 dark:text-green-300 mb-3">Final Keyword Budget</h5>
          <div class="space-y-2">
            <div v-for="kw in budgetItems" :key="kw.keyword" class="flex items-center justify-between text-sm">
              <span class="text-green-800 dark:text-green-200">{{ kw.keyword }}</span>
              <span class="font-medium" :class="kw.used >= kw.target ? 'text-green-600' : 'text-amber-500'">
                {{ kw.used }}/{{ kw.target }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Article Content Viewer -->
      <div v-if="showArticle" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {{ isNewContent ? 'Generated Article' : 'Optimized Content' }}
          </h4>
          <div class="flex items-center gap-2">
            <button
              @click="copyArticle"
              class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {{ copied ? 'Copied!' : 'Copy HTML' }}
            </button>
            <button
              @click="articleViewMode = articleViewMode === 'preview' ? 'html' : 'preview'"
              class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              {{ articleViewMode === 'preview' ? 'View HTML' : 'Preview' }}
            </button>
          </div>
        </div>
        <div class="p-6">
          <!-- Assembled content (HTML preview or raw) -->
          <div v-if="optimization.assembled_content">
            <div v-if="articleViewMode === 'preview'"
                 class="prose prose-sm dark:prose-invert max-w-none max-h-[600px] overflow-y-auto"
                 v-html="optimization.assembled_content" />
            <pre v-else class="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-4 max-h-[600px] overflow-y-auto whitespace-pre-wrap break-words">{{ optimization.assembled_content }}</pre>
          </div>
          <!-- Fallback: show individual sections -->
          <div v-else-if="contentChanges.length" class="space-y-6">
            <div v-for="(section, idx) in contentChanges" :key="section.id || idx">
              <h5 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {{ section.heading || `Section ${idx + 1}` }}
              </h5>
              <div class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {{ section.final_text || section.proposed_text || 'No content' }}
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500">No content available.</p>
        </div>
      </div>
    </div>

    <!-- Failed state -->
    <div v-else-if="optimization.status === 'failed'" class="text-center py-12">
      <div class="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h4 class="text-base font-medium text-red-800 dark:text-red-300 mb-2">Operation Failed</h4>
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">{{ optimization.error_message }}</p>
      <div class="flex items-center justify-center gap-3">
        <button
          @click="retryCurrentWorkflow"
          :disabled="starting"
          class="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ starting ? 'Starting...' : 'Retry' }}
        </button>
        <button
          @click="optimization = null"
          class="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 transition-colors">
          Choose Path
        </button>
      </div>
    </div>

    <!-- F-12: Density Audit (shown after content is completed/assembled) -->
    <PageDensityAudit
      v-if="showDensityAudit"
      :page-id="pageId"
      @audit-clean="onAuditClean"
    />

    <!-- F-13: FAQ Generation (shown after audit clean or completed) -->
    <PageFaqGeneration
      v-if="showFaqGeneration"
      :page-id="pageId"
    />

    <!-- F-14: Schema Generation (shown after finalized) -->
    <PageSchemaGeneration
      v-if="showSchemaGeneration"
      :page-id="pageId"
    />

    <!-- F-16: AIO Optimization (shown after schema approved) -->
    <PageAioOptimization
      v-if="showAioOptimization"
      :page-id="pageId"
    />

    <!-- F-15: Final Audit & Scoring (shown after schema approved) -->
    <PageFinalAudit
      v-if="showFinalAudit"
      :page-id="pageId"
      :can-start="optimization?.status === 'schema_approved'"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import PageDensityAudit from './page_density_audit.vue'
import PageFaqGeneration from './page_faq_generation.vue'
import PageSchemaGeneration from './page_schema_generation.vue'
import PageFinalAudit from './page_final_audit.vue'
import PageAioOptimization from './page_aio_optimization.vue'
import {
  runContentOptimization,
  getContentOptimization,
  runContentCreation
} from '~/javascript/api/content_optimization.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const optimization = ref(null)
const loading = ref(true)
const error = ref(null)
const starting = ref(false)
const progressMessage = ref('')
const preAudit = ref(null)
const showArticle = ref(false)
const articleViewMode = ref('preview')
const copied = ref(false)
const showCostBreakdown = ref(false)

let subscription = null
let pollTimer = null

const isNewContent = computed(() => optimization.value?.content_mode === 'new')

const COMPLETED_STATUSES = ['completed', 'auditing', 'audit_clean', 'generating_faqs', 'faqs_ready', 'faq_corrections_ready', 'finalized', 'generating_schema', 'schema_ready', 'schema_approved', 'running_final_audit', 'final_audit_ready', 'deliverable_accepted']
const isContentCompleted = computed(() => COMPLETED_STATUSES.includes(optimization.value?.status))

const PIPELINE_STATUSES = ['pending', 'mapping', 'imported', 'optimizing', 'generating_outline', 'outline_ready', 'writing', 'humanizing', 'assembling']
const isPipelineRunning = computed(() => PIPELINE_STATUSES.includes(optimization.value?.status))

const contentChanges = computed(() => optimization.value?.sections || [])

const displayTotalWords = computed(() => optimization.value?.original_word_count || optimization.value?.total_words_added || 0)

const budgetItems = computed(() => {
  if (optimization.value?.budget_summary) return optimization.value.budget_summary
  const budget = optimization.value?.keyword_budget || {}
  return Object.entries(budget).map(([kw, data]) => ({
    keyword: kw,
    target: data.target,
    used: data.used,
    remaining: data.remaining,
    status: data.status
  }))
})

const keywordsMetCount = computed(() => {
  return budgetItems.value.filter(kw => kw.used >= kw.target).length
})

const showDensityAudit = computed(() => {
  const status = optimization.value?.status
  return status && COMPLETED_STATUSES.includes(status)
})

const showFaqGeneration = computed(() => {
  const status = optimization.value?.status
  return status && ['audit_clean', 'generating_faqs', 'faqs_ready', 'faq_corrections_ready', 'finalized', 'generating_schema', 'schema_ready', 'schema_approved', 'running_final_audit', 'final_audit_ready', 'deliverable_accepted'].includes(status)
})

const showSchemaGeneration = computed(() => {
  const status = optimization.value?.status
  return status && ['finalized', 'generating_schema', 'schema_ready', 'schema_approved', 'running_final_audit', 'final_audit_ready', 'deliverable_accepted'].includes(status)
})

const showAioOptimization = computed(() => {
  const status = optimization.value?.status
  return status && ['schema_approved', 'running_final_audit', 'final_audit_ready', 'deliverable_accepted'].includes(status)
})

const showFinalAudit = computed(() => {
  const status = optimization.value?.status
  return status && ['schema_approved', 'running_final_audit', 'final_audit_ready', 'deliverable_accepted'].includes(status)
})

function onAuditClean() {
  fetchOptimization()
}

function formatCost(cents) {
  return (cents / 100).toFixed(4)
}

function formatStepName(step) {
  if (!step) return 'Unknown'
  return step.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const statusClass = computed(() => {
  const map = {
    pending: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    imported: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    mapping: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    optimizing: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    generating_outline: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    outline_ready: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    writing: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
    humanizing: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
    assembling: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    completed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    auditing: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    audit_clean: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    generating_faqs: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
    faqs_ready: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    faq_corrections_ready: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    finalized: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    generating_schema: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
    schema_ready: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    schema_approved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    running_final_audit: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    final_audit_ready: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    deliverable_accepted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  }
  return map[optimization.value?.status] || 'bg-gray-100 text-gray-700'
})

const statusLabel = computed(() => {
  const map = {
    pending: 'Starting',
    imported: 'Imported',
    mapping: 'Mapping',
    optimizing: 'Optimizing',
    generating_outline: 'Generating Outline',
    outline_ready: 'Outline Ready',
    writing: 'Writing',
    humanizing: 'Humanizing',
    assembling: 'Assembling',
    completed: 'Completed',
    auditing: 'Auditing',
    audit_clean: 'Audit Clean',
    generating_faqs: 'Generating FAQs',
    faqs_ready: 'FAQs Ready',
    faq_corrections_ready: 'FAQ Corrections Ready',
    finalized: 'Finalized',
    generating_schema: 'Generating Schema',
    schema_ready: 'Schema Ready',
    schema_approved: 'Schema Approved',
    running_final_audit: 'Running Final Audit',
    final_audit_ready: 'Final Audit Ready',
    deliverable_accepted: 'Deliverable Accepted',
    failed: 'Failed'
  }
  return map[optimization.value?.status] || optimization.value?.status || ''
})

const copyArticle = async() => {
  const content = optimization.value?.assembled_content
  if (!content) return
  try {
    await navigator.clipboard.writeText(content)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (_e) {
    const ta = document.createElement('textarea')
    ta.value = content
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const response = await getContentOptimization(props.pageId)
      const o = response.data.content_optimization
      if (o && !PIPELINE_STATUSES.includes(o.status)) {
        optimization.value = o
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const fetchOptimization = async() => {
  try {
    loading.value = true
    const response = await getContentOptimization(props.pageId)
    optimization.value = response.data.content_optimization
  } catch (e) {
    if (e.response?.status !== 404) {
      console.error('Error fetching content optimization:', e) // eslint-disable-line no-console
    }
    optimization.value = null
  } finally {
    loading.value = false
  }
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'ContentOptimizationChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (['completed', 'failed'].includes(data.step)) {
          stopPolling()
          fetchOptimization()
          if (data.pre_audit) preAudit.value = data.pre_audit
        }
      }
    }
  )
}

const startOptimization = async() => {
  try {
    starting.value = true
    error.value = null
    await runContentOptimization(props.pageId)
    subscribeToChannel()
    optimization.value = { status: 'pending', content_mode: 'existing' }
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to start content optimization'
    console.error('Failed to start content optimization:', e) // eslint-disable-line no-console
  } finally {
    starting.value = false
  }
}

const startContentCreation = async() => {
  try {
    starting.value = true
    error.value = null
    await runContentCreation(props.pageId)
    subscribeToChannel()
    optimization.value = { status: 'pending', content_mode: 'new' }
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to start content creation'
    console.error('Failed to start content creation:', e) // eslint-disable-line no-console
  } finally {
    starting.value = false
  }
}

const retryCurrentWorkflow = async() => {
  if (isNewContent.value) {
    await startContentCreation()
  } else {
    await startOptimization()
  }
}

onMounted(() => {
  fetchOptimization()
  subscribeToChannel()
})

onUnmounted(() => {
  if (subscription) {
    subscription.unsubscribe()
    subscription = null
  }
  stopPolling()
})
</script>
